import { Fragment, PureComponent, ReactNode } from 'react';
import { getDoc } from '../../store/idbHandler';
import { Patient, getPtFullNameWithSalutation } from '../../store/types/patient';
import AddRelationsModal from './AddRelationsModal';
import { ThunkDispatch } from 'redux-thunk';
import { Actions } from '../../store/actions';
import { GlobalState } from '../../store/reducers';
import fetchPtMedHistory from '../../api/fetchPtMedHistory';
import {
    LifestyleHabits,
    MHC,
    MHItem,
    PatientMedicalCondition,
} from '../../store/types/medicalHistory';
import PatientMedicalSummary, {
    getMergedMHResults,
} from '../prescriptionPad/leftPanel/PatientMedicalSummary';
import { MedicalHistory, editIcon, familyIcon } from '../common/Icons';
import { GO_TO_PATIENTS_DETAILS } from '../../routes/paths';
import { FAMILY_MEMBERS_ID_TO_NAME } from '../../constants';
import { getPatientAndRelationPatients } from './utils';
import { track } from '../../tracking';
import { GenderAndAgeComponent } from '../common/GenderAndAgeComponent';
import { History } from 'history';
import { PtListItemViewOnly, PtListItemViewOnlyContent } from './PtListItem';
import { getSalutation } from '../../pages/utils';

interface Props {
    pid: string;
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    closeMe: () => void;
    history: History;
    from: string;
}

type State = {
    patient?: Patient;
    relationPatients: Patient[];
    isAddRelationsModalOpen?: boolean;
    ptMedicalHistory?: MHC;
    ptMedicalCondition?: PatientMedicalCondition[];
    ptLifeStyleHabits?: LifestyleHabits[];
};

class RelationsDetail extends PureComponent<Props, State> {
    state: State = { relationPatients: [] };

    componentDidMount = () => {
        const { getPatientDetails, fetchPtMedicalHistory } = this;
        getPatientDetails();
        fetchPtMedicalHistory();
    };

    getPatientDetails = async () => {
        const { pid } = this.props;
        const patientAndRelationPatients = await getPatientAndRelationPatients(pid);
        if (!patientAndRelationPatients) {
            track('ERROR', { on: 'getPatientAndRelationPatients' });
            return;
        }
        const { patient, relationPatients } = patientAndRelationPatients;
        this.setState({ patient, relationPatients });
    };

    toggleIsAddRelationsModalOpen = () => {
        const { closeMe } = this.props;
        const { isAddRelationsModalOpen } = this.state;
        this.setState({ isAddRelationsModalOpen: !isAddRelationsModalOpen });
        if (isAddRelationsModalOpen) closeMe();
    };

    fetchPtMedicalHistory = (): void => {
        const { pid } = this.props;
        fetchPtMedHistory(getDoc()?._id || '', pid)
            .then((d) => {
                d.data
                    ? this.setState({
                          ptMedicalHistory: d.data,
                          ptMedicalCondition: d.data.patientMedicalConditions || [],
                          ptLifeStyleHabits: d.data.lifestyleHabits || [],
                      })
                    : this.setState({
                          ptMedicalHistory: {},
                          ptMedicalCondition: [],
                          ptLifeStyleHabits: [],
                      });
            })
            .catch(() => {
                this.setState({
                    ptMedicalHistory: {},
                    ptMedicalCondition: [],
                    ptLifeStyleHabits: [],
                });
            });
    };

    onPatientsDetails = (id: string): void => {
        const { history } = this.props;
        history.push(GO_TO_PATIENTS_DETAILS(id, 'visit', ''));
    };

    render(): ReactNode {
        const { from } = this.props;
        const {
            patient,
            isAddRelationsModalOpen,
            relationPatients,
            ptMedicalCondition,
            ptLifeStyleHabits,
        } = this.state;
        const { toggleIsAddRelationsModalOpen, onPatientsDetails } = this;
        const formData = patient?.formData?.filter((i) => i.value);

        if (!patient) {
            return (
                <div
                    className="flex items-center justify-center w-320 overflow-auto p-16 rounded-16 space-y-16"
                    style={{ minHeight: '225px', maxHeight: '75vh' }}
                >
                    <p className="text-red-primary w-172 text-center text-13">
                        Failed to fetch latest details of the patient
                    </p>
                </div>
            );
        }

        const mergedResult: MHItem[] = getMergedMHResults(ptMedicalCondition, ptLifeStyleHabits);

        return (
            <Fragment>
                <div
                    className="w-320 overflow-auto p-16 rounded-16 space-y-16"
                    style={{ minHeight: '225px', maxHeight: '70vh' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div>
                        <PtListItemViewOnly pt={patient} />
                        <PtListItemViewOnlyContent pt={patient} className="pl-28" />
                    </div>
                    {formData && formData.length > 0 && (
                        <div className="px-12 py-8 bg-darwin-neutrals-50 rounded-12">
                            {formData.map((i, index) => (
                                <div
                                    className={`flex justify-between ${
                                        formData?.length !== index + 1 ? 'border-b' : ''
                                    } border-darwin-neutral-100 text-12 py-6`}
                                >
                                    <p>{i.label}</p>
                                    <p>{i.value}</p>
                                </div>
                            ))}
                        </div>
                    )}
                    {mergedResult.length > 0 && (
                        <div className="space-y-8">
                            <div className="flex justify-between">
                                <div className="flex items-center space-x-8">
                                    {MedicalHistory('w-12 h-13 fill-darwin-neutrals-600')}
                                    <p className="text-12 text-darwin-neutrals-600">
                                        Patient’s medical history
                                    </p>
                                </div>
                            </div>
                            {(ptMedicalCondition || ptLifeStyleHabits) && (
                                <div className="px-12 pt-4 pb-10  bg-darwin-neutrals-50 rounded-12">
                                    <PatientMedicalSummary
                                        patientMedicalCondition={ptMedicalCondition}
                                        patientLifeStyleHabits={ptLifeStyleHabits}
                                        showAll
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    <div className="space-y-8">
                        <div className="flex justify-between">
                            <div className="flex items-center space-x-8">
                                {familyIcon('w-16 h-13 fill-darwin-neutrals-600')}
                                <p className="text-12 text-darwin-neutrals-600">
                                    {relationPatients.length} related profile
                                </p>
                            </div>
                            {relationPatients.length > 0 && (
                                <button
                                    className="flex items-center space-x-8"
                                    onClick={toggleIsAddRelationsModalOpen}
                                >
                                    <p>
                                        {editIcon('w-14 h-14 fill-darwin-accent-bright-blue-800')}
                                    </p>
                                    <p className="text-darwin-accent-bright-blue-800 font-700 text-13">
                                        Add or remove
                                    </p>
                                </button>
                            )}
                        </div>
                        {relationPatients && relationPatients.length > 0 ? (
                            <div className="px-12 py-4 bg-darwin-neutrals-50 rounded-12">
                                {relationPatients.map((pt, index) => {
                                    const relation = patient?.relations?.find(
                                        (r) => r.pid === pt.localId,
                                    )?.rid;
                                    const ptNameText = getPtFullNameWithSalutation(pt);
                                    return (
                                        <div
                                            className={`flex justify-between ${
                                                relationPatients?.length !== index + 1
                                                    ? 'border-b'
                                                    : ''
                                            } border-darwin-neutral-100 text-12 py-6`}
                                        >
                                            <p className="text-darwin-neutral_new-800 text-12">
                                                {FAMILY_MEMBERS_ID_TO_NAME[relation || ''] || '-'}
                                            </p>
                                            <div className="flex flex-col items-end space-y-3">
                                                <p className="text-darwin-neutrals-1000 text-13">
                                                    <span
                                                        className="underline cursor-pointer"
                                                        onClick={() =>
                                                            onPatientsDetails(pt.localId)
                                                        }
                                                    >
                                                        {ptNameText}
                                                    </span>{' '}
                                                    {pt.archived && <span>(Archived)</span>}
                                                </p>
                                                <p className="text-darwin-neutral_new-800 text-11">
                                                    <GenderAndAgeComponent pt={pt} />
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <button
                                className="w-full px-12 py-10 flex items-center justify-between bg-darwin-neutrals-50 rounded-12"
                                onClick={toggleIsAddRelationsModalOpen}
                            >
                                <p className="text-13 font-700 text-darwin-accent-bright-blue-800">
                                    Add related patient
                                </p>
                                <svg
                                    viewBox="0 0 12 11"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-12 h-11 fill-darwin-accent-bright-blue-800"
                                >
                                    <path d="M11.4844 5.58203L7.01562 9.84766C6.76172 10.0762 6.38086 10.0762 6.15234 9.82227C5.92383 9.56836 5.92383 9.1875 6.17773 8.95898L9.55469 5.73438H0.921875C0.566406 5.73438 0.3125 5.48047 0.3125 5.125C0.3125 4.79492 0.566406 4.51562 0.921875 4.51562H9.55469L6.17773 1.31641C5.92383 1.08789 5.92383 0.681641 6.15234 0.453125C6.38086 0.199219 6.78711 0.199219 7.01562 0.427734L11.4844 4.69336C11.6113 4.82031 11.6875 4.97266 11.6875 5.125C11.6875 5.30273 11.6113 5.45508 11.4844 5.58203Z" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>

                {patient.id && isAddRelationsModalOpen && (
                    <AddRelationsModal
                        pid={patient.id}
                        closeMe={toggleIsAddRelationsModalOpen}
                        from={from}
                    />
                )}
            </Fragment>
        );
    }
}

export default RelationsDetail;
